import moment from 'moment'
import { useTranslation } from 'react-i18next'

const formatMoney = value => {
	if (value == null || value === '') return ''
	const number = Number(value)
	if (!Number.isFinite(number)) return ''
	return number.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const headerClass = color => `px-2 py-2 text-left text-xs font-semibold text-gray-900 border border-gray-300 ${color}`

export default function RealizationTable({ rows, totals }) {
	const { t } = useTranslation()
	const items = Array.isArray(rows) ? rows : []

	return (
		<div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
			<table className="min-w-full border-collapse text-sm">
				<thead>
					<tr>
						<th className={headerClass('bg-gray-100')}>{t('rozliczenia.details.table.date')}</th>
						<th className={headerClass('bg-yellow-100')}>{t('rozliczenia.details.table.departure')}</th>
						<th className={headerClass('bg-yellow-100')}>{t('rozliczenia.details.table.destination')}</th>
						<th className={headerClass('bg-yellow-100')}>{t('rozliczenia.details.table.accommodation')}</th>
						<th className={headerClass('bg-yellow-100')}>{t('rozliczenia.details.table.ratePerKm')}</th>
						<th className={headerClass('bg-yellow-100')}>{t('rozliczenia.details.table.km')}</th>
						<th className={headerClass('bg-sky-100')}>{t('rozliczenia.details.table.stepName')}</th>
						<th className={headerClass('bg-sky-100')}>{t('rozliczenia.details.table.projectName')}</th>
						<th className={headerClass('bg-sky-100')}>{t('rozliczenia.details.table.group')}</th>
						<th className={headerClass('bg-sky-100')}>{t('rozliczenia.details.table.hourlyRate')}</th>
						<th className={headerClass('bg-sky-100')}>{t('rozliczenia.details.table.hours')}</th>
						<th className={headerClass('bg-blue-200')}>{t('rozliczenia.details.table.realizationTotal')}</th>
						<th className={headerClass('bg-green-100')}>{t('rozliczenia.details.table.accommodationCost')}</th>
						<th className={headerClass('bg-green-100')}>{t('rozliczenia.details.table.additionalCosts')}</th>
						<th className={headerClass('bg-orange-100')}>{t('rozliczenia.details.table.kmTotal')}</th>
					</tr>
				</thead>
				<tbody>
					{items.length === 0 ? (
						<tr>
							<td colSpan={15} className="px-3 py-8 text-center text-gray-500 border border-gray-200">
								{t('rozliczenia.details.emptyRealizations')}
							</td>
						</tr>
					) : items.map((row, index) => {
						const date = moment(row.date)
						const isWeekend = date.day() === 0 || date.day() === 6
						const rowClass = isWeekend ? 'bg-red-200' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')

						return (
							<tr key={`${row.projectId}-${row.stepId}-${index}`} className={rowClass}>
								<td className="whitespace-nowrap border border-gray-200 px-2 py-1.5">{date.format('DD.MM.YYYY')}</td>
								<td className="border border-gray-200 px-2 py-1.5">{row.miejsce_wyjazdu || ''}</td>
								<td className="border border-gray-200 px-2 py-1.5 min-w-48">{row.miejsce_docelowe || ''}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-center">{row.nocleg ? 'TAK' : ''}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.stawka_za_km)}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.liczba_km)}</td>
								<td className="border border-gray-200 px-2 py-1.5 min-w-40">
									{row.projectId && row.nazwa_etapu ? (
										<a
											href={`/centrumSzkolen/projekty/details/${row.projectId}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:text-blue-800 hover:underline"
										>
											{row.nazwa_etapu}
										</a>
									) : (row.nazwa_etapu || '')}
								</td>
								<td className="border border-gray-200 px-2 py-1.5 min-w-40">
									{row.projectId && row.nazwa_projektu ? (
										<a
											href={`/centrumSzkolen/projekty/details/${row.projectId}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:text-blue-800 hover:underline"
										>
											{row.nazwa_projektu}
										</a>
									) : (row.nazwa_projektu || '')}
								</td>
								<td className="border border-gray-200 px-2 py-1.5 text-center">{row.grupa || ''}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.stawka_godzinowa)}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.liczba_godzin)}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right font-medium">{formatMoney(row.kwota_realizacji)}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.koszt_noclegu)}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.dodatkowe_koszty)}</td>
								<td className="border border-gray-200 px-2 py-1.5 text-right">{formatMoney(row.suma_km)}</td>
							</tr>
						)
					})}
				</tbody>
				<tfoot>
					<tr className="bg-gray-100 font-semibold">
						<td colSpan={11} className="border border-gray-300 px-2 py-2 text-right">
							{t('rozliczenia.details.totalsLabel')}
						</td>
						<td className="border border-gray-300 px-2 py-2 text-right">{formatMoney(totals?.kwota_realizacji)}</td>
						<td className="border border-gray-300 px-2 py-2 text-right">{formatMoney(totals?.koszt_noclegu)}</td>
						<td className="border border-gray-300 px-2 py-2 text-right">{formatMoney(totals?.dodatkowe_koszty)}</td>
						<td className="border border-gray-300 px-2 py-2 text-right">{formatMoney(totals?.suma_km)}</td>
					</tr>
					<tr className="bg-indigo-50 font-semibold">
						<td colSpan={11} className="border border-gray-300 px-2 py-2 text-right">
							{t('rozliczenia.details.toSettleLabel')}
						</td>
						<td colSpan={4} className="border border-gray-300 px-2 py-2 text-right text-base">
							{formatMoney(totals?.do_rozliczenia)} PLN
						</td>
					</tr>
				</tfoot>
			</table>
		</div>
	)
}
